import './TubesCleanedGraph.scss';

import { IonButton, IonCard, IonCardContent, IonCardHeader, IonIcon, IonRow, useIonViewDidLeave } from '@ionic/react';
import classNames from 'classnames';
import { informationCircle, resize } from 'ionicons/icons';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { assertNever } from 'src/utils/assert';
import { useBreakpointMap } from 'src/utils/helpers/platformHelpers';
import { TimeTable } from 'src/utils/logs/logHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { JobMessages } from '../../intl/JobMessages';
import { AnnotationViewModel, useAnnotationsContext } from '../annotations/AnnotationsContext';
import { JobLogAnnotations } from '../annotations/JobLogAnnotations';
import { DqText } from '../dq-text';
import { LogTimeDisplay, TubesCleanedGraphController } from './TubesCleanedGraphController';
import TubesCleanedModal from './information/information-modals/TubesCleanedModal';
import { TubesCleanedGraphTranslator } from './util/TubesCleanedGraphTranslator';
import { TubesCleanedGraphDataElement, TubesCleanedGraphDataSegment } from './util/types';
import { InputSelect } from '../reusable-components/InputSelect';
import { RangeSet } from '../visualizations/utils';

interface TubesCleanedGraphProps {
  /** The tube count data from the job log. */
  tubeCount: JobLogV2Data['tubeCount'];
  /** The relative/absolute time table from the job log. */
  timeTable: JobLogV2Data['timeTable'];
  /** Time range min value */
  timeRangeMin: number;
  /** Time range max value */
  timeRangeMax: number;
  /** The timezone associated with the job log. */
  timezone: string;
  /** Whether to show annotations */
  showAnnotations: boolean;
  showTimestamps: boolean;
  showAnnotationActions: boolean;
  showAnnotationUser: boolean;
  orgName: string | undefined;
  controllerOffRanges: RangeSet;
}

export const TubesCleanedGraph: FC<TubesCleanedGraphProps> = props => {
  const [selectedTimestamp, setSelectedTimestamp] = useState<number | undefined>();

  const time = useMemo<TimeTable | undefined>(() => {
    if (!props.timeTable?.length) return undefined;
    return TimeTable.from(props.timeTable);
  }, [props.timeTable]);

  /** The view model data for the tubes cleaned graph. */
  const graphData = useMemo<TubesCleanedGraphDataSegment>(() => {
    if (!time || !props.tubeCount) return [];
    return TubesCleanedGraphTranslator.parseTubeCount(
      props.tubeCount,
      time,
      props.timeRangeMin,
      props.timeRangeMax,
      props.controllerOffRanges,
    );
  }, [time, props.tubeCount, props.timeRangeMin, props.timeRangeMax, props.controllerOffRanges]);

  const timeConverter = useMemo(() => {
    if (!time) return undefined;

    return (value: number, from: LogTimeDisplay, to: LogTimeDisplay): number => {
      if (from === LogTimeDisplay.Calendar) {
        switch (to) {
          case LogTimeDisplay.Calendar:
            return value;
          case LogTimeDisplay.Runtime:
            return time.getMsElapsed(value);
          default:
            return assertNever(to);
        }
      } else if (from === LogTimeDisplay.Runtime) {
        switch (to) {
          case LogTimeDisplay.Calendar:
            return time.getInstant(value);
          case LogTimeDisplay.Runtime:
            return value;
          default:
            return assertNever(to);
        }
      } else {
        return assertNever(from);
      }
    };
  }, [time]);

  return (
    <TubesCleanedGraphDisplay
      graphData={graphData}
      tubeCount={props.tubeCount}
      timeConverter={timeConverter}
      timezone={props.timezone}
      showTimestamps={props.showTimestamps}
      setSelectedTimestamp={setSelectedTimestamp}
    >
      {props.showAnnotations && (
        <span>
          <JobLogAnnotations
            timestamp={selectedTimestamp}
            timezone={props.timezone}
            showTimestamps={props.showTimestamps}
            showAnnotationActions={props.showAnnotationActions}
            showAnnotationUser={props.showAnnotationUser}
            fallbackUser={props.orgName}
          />
        </span>
      )}
    </TubesCleanedGraphDisplay>
  );
};

interface TubesCleanedGraphDisplayProps {
  /** The view model data for the tubes cleaned graph. */
  graphData: TubesCleanedGraphDataSegment;
  /** The tube count data from the job log. */
  tubeCount: JobLogV2Data['tubeCount'];
  /** A function that converts from one time display value to another. */
  timeConverter: ((value: number, from: LogTimeDisplay, to: LogTimeDisplay) => number) | undefined;
  /** The timezone associated with the job log. */
  timezone: string;
  /** Whether timestamps should appear in the graph. */
  showTimestamps: boolean;
  /** A function that is called when the user selects a specific point in time. */
  setSelectedTimestamp: (timestamp: number | undefined) => void;
}

const TubesCleanedGraphDisplay: FC<TubesCleanedGraphDisplayProps> = ({
  graphData,
  tubeCount,
  timeConverter,
  timezone,
  showTimestamps,
  setSelectedTimestamp,
  children,
}) => {
  const intl = useIntl();
  const [helpDialogOpen, setHelpDialogOpen] = useState<boolean>(false);

  const [xAxisType, setXAxisType] = useState(LogTimeDisplay.Calendar);
  const { annotations } = useAnnotationsContext();
  const [isZoomed, setZoomed] = useState(false);
  const aspectRatio = useBreakpointMap({ xs: 1.5, sm: 2.5, md: 3.5 });

  /** Ref object that contains the element under which the chart is created. */
  const graphRef = React.useRef<SVGSVGElement>(null);
  /** The controller object managing the svg. */
  const [ctrlRef, setCtrlRef] = useState<TubesCleanedGraphController | null>(null);

  const tooltipAccessor = useCallback(
    (at: number, item: TubesCleanedGraphDataElement, annotation?: AnnotationViewModel) => {
      const textLines = [
        new Date(at).toLocaleString(intl?.locale, { timeZone: timezone, timeZoneName: 'short' }),
        `${intl.formatMessage(JobMessages.cleanedLabel)}: ${item.cleaned.toLocaleString()}`,
        `${intl.formatMessage(JobMessages.uncleanedLabel)}: ${item.uncleaned.toLocaleString()}`,
      ];
      if (annotation) textLines.push(annotation.comment);
      if (!showTimestamps) return textLines.slice(1);
      return textLines;
    },
    [intl, showTimestamps, timezone],
  );

  // Effect that creates the svg graph element on mount
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) {
      console.warn('The component mounted but chartRef was null! The graph will not draw.');
      return;
    }
    const ctrl = new TubesCleanedGraphController(graph);
    ctrl.setOnZoom(setZoomed);
    ctrl.setOnTimestampChange(setSelectedTimestamp);
    setCtrlRef(ctrl);

    const observer = new ResizeObserver(ctrl.onResize.bind(ctrl));
    if (graph.parentElement) observer.observe(graph.parentElement);

    return () => {
      observer.disconnect();
      ctrl.setOnZoom(() => {});
      ctrl.setOnTimestampChange(() => {});
      ctrl.setTooltipAccessor(() => []);
    };
  }, [setSelectedTimestamp]);

  useIonViewDidLeave(() => {
    ctrlRef?.reset();
  }, [ctrlRef]);

  // An effect that updates the display configuration each time it changes.
  useEffect(() => {
    ctrlRef?.setDisplayConfiguration(intl, xAxisType, timezone, !showTimestamps);
  }, [ctrlRef, intl, showTimestamps, timezone, xAxisType]);

  // An effect that sets the time converter each time it changes.
  useEffect(() => {
    ctrlRef?.setTimeConverter(timeConverter);
  }, [ctrlRef, timeConverter]);

  // An effect that updates the aspect ratio of the graph as the breakpoints change.
  useEffect(() => {
    ctrlRef?.setAspectRatio(aspectRatio);
  }, [aspectRatio, ctrlRef]);

  useEffect(() => {
    ctrlRef?.setTooltipAccessor(tooltipAccessor);
  }, [ctrlRef, tooltipAccessor]);

  const [cleanTubes, totalTubes] = useMemo<[string, string]>(() => {
    const lastElement = graphData.slice(-1).pop();
    const cleanedTubes = lastElement?.cleaned ?? 0;
    const totalTubes = lastElement?.total ?? 0;

    return [cleanedTubes.toLocaleString(), totalTubes.toLocaleString()];
  }, [graphData]);

  // Effect that updates the graph each time the data changes.
  useEffect(() => {
    ctrlRef?.setData(graphData);
  }, [ctrlRef, graphData]);

  // Effect that updates the graph each time the annotations change.
  useEffect(() => {
    ctrlRef?.setAnnotations(annotations);
  }, [annotations, ctrlRef]);

  const handleResetZoom = useCallback(() => {
    ctrlRef?.resetZoom();
  }, [ctrlRef]);

  const jobTimeSelectOptions = [
    { value: LogTimeDisplay.Calendar, label: intl.formatMessage(JobMessages.jobTimeOptionCalendar) },
    { value: LogTimeDisplay.Runtime, label: intl.formatMessage(JobMessages.jobTimeOptionRun) },
  ];

  return (
    <IonCard>
      <IonCardHeader>
        <IonRow className="ion-justify-content-between ion-align-items-center">
          <div className="ion-flex ion-flex-direction-column ion-align-items-start">
            <DqText variant="h3">
              <FormattedMessage {...JobMessages.tubesCleanedLabel} />
            </DqText>
            <DqText variant="h2">
              {cleanTubes}/{totalTubes}
            </DqText>
          </div>
          <div>
            {isZoomed && (
              <IonButton fill="clear" onClick={handleResetZoom}>
                <IonIcon slot="icon-only" icon={resize} />
              </IonButton>
            )}
            <IonButton fill="clear" color="primary" onClick={() => setHelpDialogOpen(true)}>
              <IonIcon slot="icon-only" icon={informationCircle} />
            </IonButton>
          </div>
        </IonRow>
      </IonCardHeader>
      <IonCardContent>
        <div className="tubes-cleaned-graph-layout">
          <div className="graph-label" data-id="axis-label-y">
            <DqText variant="h3">
              <FormattedMessage {...JobMessages.tubesCleanedLabel} />
            </DqText>
          </div>
          <div data-id="content">
            <svg
              className={classNames(!showTimestamps && 'tube-card-timestamp-no-print')}
              ref={graphRef}
              preserveAspectRatio="xMinYMax meet"
            />
          </div>
          <div className="graph-label x-axis" data-id="axis-label-x">
            <InputSelect<LogTimeDisplay>
              label={intl.formatMessage(JobMessages.elapsedTime)}
              value={xAxisType}
              options={jobTimeSelectOptions}
              onChange={e => setXAxisType(e)}
              name="xAxisOptions"
              id="tubes-cleaned-select-elapsed-time"
            />
          </div>
        </div>
        <div className="tube-card-footer">
          <div className="tube-graph-key-unit">
            <div className="tube-clean-color-key tube-clean-color-one" />
            <p>
              <FormattedMessage {...JobMessages.tubesCleanedLabel} />
            </p>
          </div>
          <div className="tube-graph-key-unit">
            <div className="tube-clean-color-key tube-clean-color-two" />
            <p>
              <FormattedMessage {...JobMessages.tubesPartiallyCleanedLabel} />
            </p>
          </div>
        </div>
        {children}
      </IonCardContent>
      <TubesCleanedModal isOpen={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </IonCard>
  );
};
